<template>
    <UiPopup
        class="ui-text-input-suggestions"
        :model-value="showed"
        placement="bottom"
        align="start"
        @update:model-value="hasFocus = false"
        @click-outside="hasFocus = false"
    >
        <template #activator>
            <UiTextInput
                ref="suggestions"
                :id="id"
                v-model="localValue"
                :name="name"
                :dark="dark"
                :error="error"
                autocomplete="new-password"
                :disabled="disabled"
                @update:model-value="hasFocus = true"
                @blur="() => { if (!showed) hasFocus = false; }"
                @click.native="hasFocus = true"
                @keydown.down.native.prevent="focusItem(0)"
                @keydown.up.native.prevent="focusItem(items.length - 1)"
            />
        </template>
        <ul
            ref="options"
            class="ui-text-input-suggestions__popup"
            :class="{
                'ui-text-input-suggestions__popup_dark': dark,
            }"
            tabindex="-1"
        >
            <li
                v-for="(item, key) in items"
                :key="key"
                :ref="`option-${key}`"
                class="ui-text-input-suggestions__option"
                tabindex="-1"
                @click="onSelect(item)"
                @keydown.down.exact.prevent="focusItem(key + 1)"
                @keydown.up.exact.prevent="focusItem(key - 1)"
                @keydown.tab.exact.prevent="focusNextSibling"
                @keydown.tab.shift.prevent="focusPrevSibling"
                @keydown.space.exact.prevent="onSelect(item)"
                @keydown.enter.exact.prevent="onSelect(item)"
            >
                <slot name="item" :item="item">{{ item.value }}</slot>
            </li>
        </ul>
    </UiPopup>
</template>
<script>
import UiTextInput from '../../components/ui/UiTextInput.vue';
import UiPopup from './UiPopup.vue';
import { dadataApi } from '../../assets/js/vue/utlis/useDadata';
import { ESuggestionTypes } from '../../assets/js/vue/utlis/useDadata/utils/ESuggestionTypes';
import { EAddressBounds } from '../../assets/js/vue/utlis/useDadata/utils/EAddressBounds';
import { EFioParts } from '../../assets/js/vue/utlis/useDadata/utils/EFioParts';
import { ref, watch } from 'vue';
import { useResizeObserver } from '../../assets/js/vue/utlis/useResizeObserver';
import fastdom from 'fastdom';
import debounce from 'lodash/debounce';

export default {
    name: 'UiTextInputSuggestions',
    components: {
        UiTextInput,
        UiPopup,
    },
    props: {
        id: String,
        classNames: { type: String, default: ''},
        modelValue: String,
        /**
         * suggestion option: **type**
         */
        suggestionType: {
            type: String,
            validator: (val) => Object.values(ESuggestionTypes).includes(val),
        },
        /**
         * suggestion option: **params.parts**
         */
        suggestionPatrs: {
            type: Array,
            default: () => [],
            validator: (val) => val.every((part) => Object.values(EFioParts).includes(part)),
        },
        /**
         * suggestion option: **bounds**
         */
        bounds: {
            type: String,
            validator: (val) => Object.values(EAddressBounds).includes(val),
        },
        /**
         * Гранулярные подсказки
         * suggestion option: **bounds**
         */
        bounds_from: {
            type: String,
            validator: (val) => Object.values(EAddressBounds).includes(val),
        },
        /**
         * Гранулярные подсказки
         * suggestion option: **bounds**
         */
        bounds_to: {
            type: String,
            validator: (val) => Object.values(EAddressBounds).includes(val),
        },
        /**
         * suggestion option: **count**
         */
        count: { type: Number, default: 5 },
        /**
         * Ограничение по родителю
         * suggestion option: **locations**
         */
        locations: {
            type: Array,
            default: () => [],
        },
        /**
         * если true то произвольный ввод не сохраняется в поле
         */
        onlySuggestion: Boolean,
        error: Boolean,
        name: String,
        dark: Boolean,
        lazy: Boolean,
        disabled: Boolean,
        /**
         * @typedef {'region'|'area'|'city'|'settlement'|'street'} TAddressType
         */
        /**
         * ограничение сектора поиска по названию адресного объекта  (suggestion option: **locations**)
         * @type {Record<TAddressType, string>[]}
         */
        minLengthSuggest: { type: Number, default: 3 },
        /**
         * Автоматически определить локацию по ip и приоретизировать поиск в этой локации
         */
        autolocation: Boolean
    },
    setup() {
        const suggestions = ref(null);
        const options = ref(null);

        const suggestionsWidth = ref(0);

        useResizeObserver(suggestions, (entries) => {
            entries.forEach((entry) => {
                let width;
                if (entry.borderBoxSize !== undefined) { // supported ios >=15.4
                    width = entry.borderBoxSize?.[0]?.inlineSize;
                } else {
                    fastdom.measure(() => {
                        width = entry.target.getBoundingClientRect().width;
                    });
                }
                fastdom.mutate(() => {
                    suggestionsWidth.value = width;
                });
            });
        }, { box: 'border-box' });

        watch([suggestionsWidth, options], ([val, optionsEl]) => {
            if (!optionsEl || !val) return;

            fastdom.mutate(() => {
                optionsEl.style.width = `${val}px`;
            });
        }, { immediate: true });

        return {
            suggestions,
            options,
            suggestionsWidth,
        };
    },
    data() {
        return {
            localValue: '',
            hasFocus: false,
            items: [],
            isUserSelect: false,
            fetchSuggestionsDebounced: debounce(this.fetchSuggestions, 1000),
            location: {},
        };
    },
    computed: {
        allowInput() {
            return this.hasFocus || !this.onlySuggestion;
        },
        showed() {
            return this.hasFocus && this.items.length > 0;
        },
        /**
         * Приоритет города при ранжировании
         */
        locations_boost() {
            let priority;

            if (this.suggestionType === ESuggestionTypes.ADDRESS && this.location?.kladr_id) {
                priority = [{
                    kladr_id: this.location.kladr_id,
                }];
            }
            return priority;
        },
        suggestionsQuery() {
            return {
                type: this.suggestionType,
                query: this.localValue,
                count: this.count,
                options: {
                    bounds_from: { value: this.bounds ?? this.bounds_from },
                    bounds_to: { value: this.bounds ?? this.bounds_to },
                    parts: this.suggestionPatrs,
                    locations_boost: this.locations_boost,
                    locations: this.locations,
                },
                locations: this.locations,
            };
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(val) {
                this.localValue = val;
            },
        },
        localValue(val) {
            if (this.modelValue !== val && !this.lazy) this.$emit('update:modelValue', val);
        },
        hasFocus(val) {
            if (!val) {
                this.checkIfSelectNone();

                if (this.modelValue !== this.localValue) this.$emit('update:modelValue', this.localValue);
            } else {
                //reset userSelect flag
                this.isUserSelect = false;
            }
        },
        suggestionsQuery(val) {
            if (val.query && val.query.length >= this.minLengthSuggest) {
                this.fetchSuggestionsDebounced(val);
            } else {
                this.items = [];
            }
        },
    },
    methods: {
        async fetchSuggestions({ type, query, count, options, restrict_value, locations }) {
            if (!this.hasFocus) return [];
            this.items = (await dadataApi.suggest(type, query, count, options, restrict_value, locations)) ?? [];
        },
        onSelect(item) {
            this.localValue = item.value;
            this.$emit('update:data', item.data);
            this.isUserSelect = true;
            this.hasFocus = false;

            if (document.activeElement !== this.$refs.suggestions.$el) this.$refs.suggestions.$el.focus();
        },
        checkIfSelectNone() {
            if (this.onlySuggestion && !this.isUserSelect) this.localValue = '';
        },
        onOtherFocus(e) {
            if (!this.hasFocus) return;
            const { target } = e;
            if (target === this.$refs.suggestions.$el) return;
            if (this.$refs.options?.contains(document.activeElement)) return;

            this.hasFocus = false;
        },
        focusItem(index) {
            const els = this.$refs[`option-${index}`];
            const el = Array.isArray(els) ? els[0] : el;
            if (!el) return;

            el.focus();
        },
        focusNextSibling() {
            this.$refs.suggestions.$el.focus();
        },
        focusPrevSibling() {
            this.$refs.suggestions.$el.focus();
        },
    },
    async created() {
        // set initial query and object value
        if (this.localValue) {
            await this.fetchSuggestions(this.suggestionsQuery);
            const firstItemData = this.items[0]?.data;
            if (firstItemData) this.$emit('update:data', firstItemData);
        }
    },
    mounted() {
        window.addEventListener('focusin', this.onOtherFocus, { passive: true });
        if (this.autolocation) this.location = dadataApi.getLocationByIp();
    },
    unmounted() {
        window.removeEventListener('focusin', this.onOtherFocus, { passive: true });
    },
};
</script>
<style lang="scss">
@import '/local/templates/redesign/src/assets/scss/base/_variables';
@import '/local/templates/redesign/src/assets/scss/base/_functions';
@import '/local/templates/redesign/src/assets/scss/base/_mixins';

/**
 * TODO: вынести сюда все стили
 */
.ui-text-input-suggestions {
    width: 100%;
    &__popup {
        --ui-text-input-suggestions__bg: #{$white-101};
        --ui-text-input-suggestions__bg_hover: #f7f7f7;
        --ui-text-input-suggestions__color: #{$text-second};

        overflow: hidden;
        @include fluid('border-radius', 20px, 16px);
        box-shadow: 5px 6px 10px rgba(0, 0, 0, 0.1);
        border: 1px solid $gray-light-hover;

        &_dark {
            --ui-text-input-suggestions__bg: #{$grey-868};
            --ui-text-input-suggestions__bg_hover: #{$grey-517};
            --ui-text-input-suggestions__color: #{$grey-134};
        }

        &_absolute {
            position: absolute;
            width: 100%;
            z-index: 10000;
        }
    }
    &__option {
        cursor: pointer;
        color: var(--ui-text-input-suggestions__color);
        background: var(--ui-text-input-suggestions__bg);
        @include fluid('padding-top', 17px, 14px);
        @include fluid('padding-bottom', 17px, 13px);
        @include fluid('padding-left', 20px, 16px);
        @include fluid('padding-right', 20px, 16px);
        transition: background .2s;
        user-select: none;

        &:not(:last-child) {
            border-bottom: 1px solid $gray-light-hover;
        }

        &:hover, &:focus {
            background: var(--ui-text-input-suggestions__bg_hover);
        }
    }
}
</style>
